import { useEffect, useMemo } from "react";

import {
  InfiniteData,
  Query,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { type Draft, produce } from "immer";
import { OpenAPIV3_1 } from "openapi-types";

import * as flowApi from "src/api";
import { flowDbToFlowT, loadFlow, loadFlows } from "src/api";
import {
  CasePatchBody,
  CreateMultiversionTestRunParams,
  FlowRouterReviewCasesEndpoint,
  flowsApi,
  ListReviewCasesParams,
  RouterMultiversionTestRun,
  trafficPoliciesApi,
  workspacesApi,
} from "src/api/endpoints";
import { Flow, FlowVersion, Organization } from "src/api/flowTypes";
import { organizationsApi } from "src/api/taktileApi";
import {
  ListReviewCasesResponse,
  ParsedModelDataError,
  ParsedModelDataPending,
  ParsedModelDataSuccess,
  ReviewCase,
  ReviewCaseWithDecisionData,
  UploadModelData,
  WorkspaceWithSettings,
} from "src/api/types";
import {
  FlowFolderListResponse,
  FlowNaming,
  FlowTags,
  GetFlowsApiV1FlowsGetOrderByEnum,
  TrafficPolicyCreateService,
  TrafficPolicyInDB,
  WorkspaceDataplane,
  WorkspaceFeatureGates,
} from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { AwsRegionType } from "src/constants/AwsRegions";
import { TAKTILE_ORG_ID } from "src/constants/OrgIds";
import { folderKeys } from "src/flowsOverview/v2/folderQueries";
import * as mlNodeApi from "src/mlNode/api/mlNodeApi";
import { UploadedModelResponse } from "src/mlNode/api/mlNodeApi";
import { queryClient } from "src/queryClient";
import { shouldShowInternalResources } from "src/router/featureFlags";
import { DashboardPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

// 5 minutes
const DEFAULT_STALE_TIME = 1000 * 60 * 5;

export const NEVER_REFETCH_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnMount: false,
  refetchInterval: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

// Flows hooks
export const flowKeys = {
  all: ["flows"] as const,
  workspaceListFiltered: (
    workspaceId: string | undefined,
    folderId: string | undefined,
    flowTags?: FlowTags | undefined,
    includeSchemas?: boolean,
  ) =>
    [...flowKeys.all, workspaceId, folderId, flowTags, includeSchemas] as const,
  workspaceListFilteredOrdered: (
    workspaceId: string,
    folderId: string | undefined,
    orderBy?: GetFlowsApiV1FlowsGetOrderByEnum | undefined,
    includeSchemas?: boolean,
    flowTags?: FlowTags | undefined,
  ) =>
    [
      ...flowKeys.workspaceListFiltered(
        workspaceId,
        folderId,
        flowTags,
        includeSchemas,
      ),
      orderBy,
    ] as const,
  detail: (id: string, includeArchivedFlowVersions?: boolean) =>
    [...flowKeys.all, id, { includeArchivedFlowVersions }] as const,
  slugDetail: (workspaceId: string, slug: string, options: string) =>
    [...flowKeys.all, workspaceId, slug, options] as const,
};

export const workspaceKeys = {
  all: ["workspaces"] as const,
  detail: (id: string, includeSettings?: boolean) =>
    [...workspaceKeys.all, id, includeSettings] as const,
  featureGates: (id: string) =>
    [...workspaceKeys.all, "featureGates", id] as const,
} as const;

const flowTrafficPoliciesKeys = {
  all: ["flowsTrafficPolicies"] as const,
  detail: (id: string) => [...flowTrafficPoliciesKeys.all, id] as const,
};

const sortByName = (data: WorkspaceDataplane[]) =>
  data
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

export const useWorkspaces = (
  refetchOnMount: boolean = false,
  includeInactive: boolean = false,
) => {
  return useQuery<WorkspaceDataplane[], Error>({
    queryKey: workspaceKeys.all,
    queryFn: () => flowApi.loadWorkspaces(includeInactive),
    refetchOnMount,
    select: sortByName,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useCurrentWorkspace = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  return useWorkspace(wsId);
};

type UseWorkspaceOptions = {
  refetchOnPending?: boolean;
  includeSettings?: boolean;
};

const REFETCH_ON_PENDING_FN = (query: Query<WorkspaceDataplane, AxiosError>) =>
  query.state.data?.status === "PENDING" ? 5000 : false;

export const useWorkspace = <TData = WorkspaceDataplane>(
  workspaceId?: string,
  options: UseWorkspaceOptions = {},

  select?: UseQueryOptions<WorkspaceDataplane, AxiosError, TData>["select"],
) => {
  return useQuery<WorkspaceDataplane, AxiosError, TData>({
    queryKey: workspaceKeys.detail(
      workspaceId!,
      options.includeSettings ?? false,
    ),
    queryFn: () => flowApi.loadWorkspace(workspaceId!, options.includeSettings),
    enabled: Boolean(workspaceId),
    refetchInterval: options.refetchOnPending
      ? REFETCH_ON_PENDING_FN
      : undefined,
    staleTime: DEFAULT_STALE_TIME,
    select,
  });
};

const selectFeatureGates = (data: WorkspaceDataplane) =>
  data.feature_gates ?? {};

const DefaultFeatureGates: WorkspaceFeatureGates = {
  entity: false,
  feature: false,
  cm_copilot: false,
  tktl_agents: false,
};

export const useWorkspaceFeatureGates = (workspaceId?: string) => {
  return (
    useWorkspace(workspaceId, { includeSettings: false }, selectFeatureGates)
      .data ?? DefaultFeatureGates
  );
};

// Speacial case for the workspace data which always includes the settings
export const useWorkspaceWithSettings = (workspaceId?: string) => {
  return useWorkspace(workspaceId, { includeSettings: true }) as UseQueryResult<
    WorkspaceWithSettings,
    Error
  >;
};

export type CreateWorkspaceParams = {
  name: string;
  slug: string;
  organizationId: string;
  awsRegion: AwsRegionType;
  awsAccountId?: string;
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceParams: CreateWorkspaceParams) =>
      flowApi.createWorkspace(
        workspaceParams.name,
        workspaceParams.slug,
        workspaceParams.organizationId,
        workspaceParams.awsRegion.identifier,
        workspaceParams.awsAccountId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
};

export type UpdateWorkspaceParams = Pick<
  WorkspaceDataplane,
  "id" | "name" | "flow_services_version" | "settings" | "is_active"
>;

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceParams: UpdateWorkspaceParams) =>
      flowApi.editWorkspace(workspaceParams),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
    },
  });
};

const FLOW_PAGE_SIZE = 100;

export const useFlows = ({
  workspaceId,
  folderId,
  orderBy,
  disabled,
  includedTypes = ["none", "case_flow"],
  includeSchemas = false,
}: {
  workspaceId: string;
  folderId?: string;
  orderBy?: GetFlowsApiV1FlowsGetOrderByEnum;
  disabled?: boolean;
  includedTypes?: string[];
  includeSchemas?: boolean;
}) => {
  const {
    data,
    hasNextPage,
    isFetching,
    fetchNextPage,
    isLoading,
    isError,
    isRefetching,
    error,
  } = useInfiniteQuery({
    queryKey: flowKeys.workspaceListFilteredOrdered(
      workspaceId,
      folderId,
      orderBy,
      includeSchemas,
    ),
    queryFn: async ({ pageParam }) => {
      const skip = pageParam * FLOW_PAGE_SIZE;
      return loadFlows({
        workspaceId,
        folderId,
        orderBy,
        skip,
        limit: FLOW_PAGE_SIZE,
        include_internal_flows: shouldShowInternalResources(),
        includedTypes,
        include_schemas: includeSchemas,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // The check really should be lastPage.length < FLOW_PAGE_SIZE
      // relying on checking the length of the last page is 0, causes one extra request
      // but it's a workaround for a bug in flow api - it returns 1 less item for first page
      if (lastPage.length < 1) {
        return undefined;
      }

      return allPages.length;
    },
    enabled: !disabled && Boolean(workspaceId),
  });

  // eagerly load all pages
  useEffect(() => {
    if (!isFetching && hasNextPage && !disabled) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, disabled, fetchNextPage]);

  return useMemo(
    () => ({
      data: data?.pages.flat() ?? [],
      error: error as Error | null,
      isLoading: (data?.pages.length ?? 0) > 0 ? false : isLoading,
      isRefetching,
      isError,
      // Indicates all pages have been loaded
      isFullyLoaded: !isFetching && !hasNextPage && !isLoading,
    }),
    [data, error, isLoading, isRefetching, isError, isFetching, hasNextPage],
  );
};

type UseFlowOptions = {
  refetchOnMount?: boolean;
  refetchInterval?: number | false;
  enabled?: boolean;
  includeArchivedFlowVersions?: boolean;
};

export const useFlow = (flowId?: string, options?: UseFlowOptions) => {
  const { refetchOnMount = false, includeArchivedFlowVersions } = options ?? {};

  return useQuery<Flow, Error>({
    queryKey: flowKeys.detail(flowId!, includeArchivedFlowVersions),
    queryFn: () => loadFlow(flowId!, includeArchivedFlowVersions),
    enabled: !!flowId && (options?.enabled ?? true),
    refetchOnMount,
    refetchInterval: options?.refetchInterval,
    notifyOnChangeProps: options?.refetchInterval
      ? ["data", "error"]
      : undefined,
  });
};

type UseFlowBySlugOptions = {
  refetchOnMount?: boolean;
  versionNameFilter?: string;
  versionIdFilter?: string;
  defaultVersionFilter?: boolean;
  defaultSandboxVersionFilter?: boolean;
  minFlowVersionEtagFilter?: string;
  shallow?: boolean;
};

export const useFlowBySlug = (
  workspaceId?: string,
  slug?: string,
  options: UseFlowBySlugOptions = {},
): UseQueryResult<Flow, Error> => {
  const { refetchOnMount = false, ...requestOptions } = options;
  return useQuery<Flow, Error>({
    queryKey: flowKeys.slugDetail(
      workspaceId!,
      slug!,
      JSON.stringify(requestOptions),
    ),
    queryFn: async () => {
      const response =
        await flowsApi.getWorkspaceFlowBySlugApiV1FlowsWorkspaceIdWorkspaceIdSlugSlugGet(
          workspaceId!,
          slug!,
          requestOptions.versionNameFilter,
          requestOptions.versionIdFilter,
          requestOptions.defaultVersionFilter,
          requestOptions.defaultSandboxVersionFilter,
          requestOptions.minFlowVersionEtagFilter,
          requestOptions.shallow,
        );
      return flowDbToFlowT(response.data);
    },
    enabled: Boolean(slug) && Boolean(workspaceId),
    refetchOnMount,
  });
};

export const useAddFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: flowApi.createFlow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
};

type CopyFlowArgs = {
  sourceOrgId: string;
  flowId: string;
  flowVersionId: string;
  newName: string;
  newSlug: string;
  childFlowNamings: { [key: string]: FlowNaming };
  folderId?: string;
};

export const useCopyFlow = (targetWorkspaceId: string) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  return useMutation({
    mutationFn: async (args: CopyFlowArgs) => {
      const flowCopyReturn = (
        await flowsApi.postCopyFlowApiV1FlowsFlowIdCopyPost(args.flowId, {
          new_name: args.newName,
          new_slug: args.newSlug,
          target_workspace_id: targetWorkspaceId,
          flow_version_ids: [args.flowVersionId],
          child_flow_namings: args.childFlowNamings,
          flow_folder_id: args.folderId,
          copy_comments: true,
          list_datasets: true,
        })
      ).data;

      // Datasets can only be copied from taktile org
      if (args.sourceOrgId === TAKTILE_ORG_ID) {
        const allRemappedFlows = {
          [args.flowId]: flowCopyReturn.flow.id,
          ...flowCopyReturn.remapped_child_flow_ids,
        };
        const datasetCopyCalls = Object.entries(allRemappedFlows).flatMap(
          ([oldFlowId, newFlowId]) => {
            return flowCopyReturn.datasets_to_copy[oldFlowId].map((datasetId) =>
              flowsApi.postCopyFlowSampleDataApiV1FlowsFlowIdCopySampleDataPost(
                oldFlowId,
                {
                  target_flow_id: newFlowId,
                  dataset_ids: [datasetId],
                },
              ),
            );
          },
        );
        await Promise.all(datasetCopyCalls);
      }
    },
    onSuccess: () => {
      if (wsId === targetWorkspaceId) {
        queryClient.invalidateQueries({ queryKey: flowKeys.all });
      }
    },
  });
};

/**
 *
 * @returns The query keys mutated by the optimistic update
 */
export const optimisticallyUpdatesOnFolderMove = ({
  flowId,
  folderId,
  oldFolderId,
  workspaceId,
}: {
  flowId: string;
  folderId: string | null | undefined;
  oldFolderId: string | null | undefined;
  workspaceId: string;
}): QueryKey[] => {
  const updatedQueryKeys: QueryKey[] = [];
  if (folderId === undefined || folderId === oldFolderId)
    return updatedQueryKeys;

  // Optimistically remove the flow from its current folder
  const flowListsByOldFolder = queryClient.getQueriesData<{
    pageParams: number[];
    pages: Flow[][] | undefined;
  }>({
    queryKey: flowKeys.workspaceListFiltered(
      workspaceId,
      oldFolderId || flowApi.NO_FOLDER_FILTER,
    ),
  });
  flowListsByOldFolder.forEach(([key, data]) => {
    if (data && data.pages) {
      updatedQueryKeys.push(key);
      queryClient.setQueryData(key, {
        ...data,
        pages: data.pages.map((page) =>
          page.filter((flow) => flow.id !== flowId),
        ),
      });
    }
  });
  // Update the counts on folders
  const updatedFoldersData = queryClient.getQueryData<FlowFolderListResponse>(
    folderKeys.workspaceList(workspaceId),
  );
  if (updatedFoldersData) {
    if (folderId) {
      const folder = updatedFoldersData.folders.find(
        (folder) => folder.id === folderId,
      );
      if (folder) {
        folder.flow_count += 1;
      }
    } else {
      updatedFoldersData.flows_outside_folders_count += 1;
    }

    if (oldFolderId) {
      const folder = updatedFoldersData.folders.find(
        (folder) => folder.id === oldFolderId,
      );
      if (folder) {
        folder.flow_count -= 1;
      }
    } else {
      updatedFoldersData.flows_outside_folders_count -= 1;
    }

    updatedQueryKeys.push(folderKeys.workspaceList(workspaceId));
    queryClient.setQueryData(
      folderKeys.workspaceList(workspaceId),
      updatedFoldersData,
    );
  }

  return updatedQueryKeys;
};

export const useEditFlow = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: flowApi.updateFlow,
    onMutate: async (flowUpdate) => {
      return optimisticallyUpdatesOnFolderMove({
        flowId: flowUpdate.flowId,
        folderId: flowUpdate.flow_folder_id,
        oldFolderId: flowUpdate.old_folder_id,
        workspaceId,
      });
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
      queryClient.invalidateQueries({
        queryKey: flowKeys.detail(request.flowId),
      });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      // Prefetch the folder lists to ensure a smooth interaction when the user navigates to the new folder
      if (request.flow_folder_id) {
        queryClient.refetchQueries({
          queryKey: flowKeys.workspaceListFiltered(
            workspaceId,
            request.flow_folder_id,
          ),
        });
      }
    },
    onSettled: (_response, _error, _request, context) => {
      // Get the query keys mutated from the optimistic update and invalidate them in any case.
      context?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
  });
};

export const useDeleteFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (flowId: string) => flowApi.deleteFlow(flowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
};

export const useCreateTrafficPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      createPayload,
      activate,
    }: {
      createPayload: TrafficPolicyCreateService;
      activate: boolean;
    }) =>
      trafficPoliciesApi.createTrafficPolicyApiV1TrafficPoliciesPost(
        createPayload,
        activate,
      ),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
      queryClient.invalidateQueries({
        queryKey: flowTrafficPoliciesKeys.detail(data.createPayload.flow_id),
      });
    },
  });
};

export const useActivateTrafficPolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      trafficPolicyId,
      flowId,
    }: {
      trafficPolicyId: string;
      flowId: string;
    }) =>
      trafficPoliciesApi.activateTrafficPolicyApiV1TrafficPoliciesTrafficPolicyIdActivatePost(
        trafficPolicyId,
        flowId,
      ),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
      queryClient.invalidateQueries({
        queryKey: flowTrafficPoliciesKeys.detail(data.flowId),
      });
    },
  });
};

export const useTrafficPolicies = (flowId?: string) => {
  return useQuery<TrafficPolicyInDB[], Error>({
    queryKey: flowTrafficPoliciesKeys.detail(flowId!),
    queryFn: async () =>
      (
        await trafficPoliciesApi.listFlowTrafficPoliciesApiV1TrafficPoliciesGet(
          flowId!,
        )
      ).data,
    enabled: !!flowId,
  });
};

// Docs hooks
export const useDocs = (flowId: string, version: FlowVersion) => {
  return useQuery<OpenAPIV3_1.Document, Error>({
    queryKey: ["docs", flowId, version.id],
    queryFn: async () =>
      (
        await flowsApi.getVersionedFlowDocsApiV1FlowsFlowIdVersionsVersionIdDocsGet(
          flowId,
          version.id,
        )
      ).data,
  });
};

export const userOrganizationsQueryKey = ["userOrganizations"];

// User Organization hooks
export const useUserOrganizations = () => {
  return useQuery<OrganizationInDB[], Error>({
    queryKey: userOrganizationsQueryKey,
    queryFn: async () => {
      const orgs =
        await organizationsApi.getMyOrganizationsApiV1OrganizationsGet();
      return orgs.data
        .filter((org) => org.allows_decide_access)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    ...NEVER_REFETCH_OPTIONS,
  });
};

export const useUserOrganizationLogo = (orgId: string | null) => {
  return useQuery({
    queryKey: [...userOrganizationsQueryKey, orgId, "logo"],
    queryFn: async () => {
      try {
        if (!orgId) {
          logger.error("No orgId provided");
          return null;
        }
        return (
          await organizationsApi.getOrganizationLogoApiV1OrganizationsOrganizationIdLogoGet(
            orgId,
            undefined,
            { responseType: "blob" },
          )
        ).data;
      } catch (error) {
        // 404s are expected when there is no logo
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    refetchOnMount: false,
    retry: false,
    staleTime: Infinity,
    enabled: !!orgId,
  });
};

export const useUpdateOrganizationLogo = () => {
  return useMutation({
    mutationKey: ["updateOrganizationLogo"],
    mutationFn: ({
      organizationId,
      logo,
    }: {
      organizationId: string;
      logo: File;
    }) => {
      return organizationsApi.postOrganizationLogoApiV1OrganizationsOrganizationIdLogoPost(
        organizationId,
        logo,
      );
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...userOrganizationsQueryKey,
          variables.organizationId,
          "logo",
        ],
      });
    },
  });
};

export const useUpdateOrg = () => {
  return useMutation({
    mutationKey: ["updateOrganization"],
    mutationFn: ({
      organizationId,
      ...updatePayload
    }: {
      organizationId: string;
    } & Partial<
      Pick<
        Organization,
        "allows_signup" | "whitelabel" | "partner" | "is_active"
      >
    >) => {
      return organizationsApi.patchOrganizationApiV1OrganizationsOrganizationIdPatch(
        organizationId,
        {
          ...updatePayload,
          // We need to be able to set null but the generated types don't represent that.
          partner: updatePayload.partner as OrganizationInDB["partner"],
        },
      );
    },
    onSuccess: () =>
      queryClient
        .invalidateQueries({
          queryKey: userOrganizationsQueryKey,
        })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["allOrgs"],
          });
        }),
  });
};

export const useDeleteOrganizationLogo = () => {
  return useMutation({
    mutationKey: ["deleteOrganizationLogo"],
    mutationFn: ({ organizationId }: { organizationId: string }) => {
      return organizationsApi.deleteOrganizationLogoApiV1OrganizationsOrganizationIdLogoDelete(
        organizationId,
      );
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...userOrganizationsQueryKey,
          variables.organizationId,
          "logo",
        ],
      });
    },
  });
};

// Flow running hooks

const lastRunKeys = {
  all: ["lastRun"] as const,
  specific: (
    baseUrl: string | undefined,
    versionId: string,
    flowSlug: string,
  ) => [...lastRunKeys.all, baseUrl, versionId, flowSlug] as const,
};

export type UploadModelContext = {
  oldFileName?: string;
};

export const useUploadModelFile = (
  options: UseMutationOptions<
    UploadedModelResponse,
    AxiosError<BackendErrorData>,
    UploadModelData,
    UploadModelContext
  >,
) => {
  return useMutation({
    mutationFn: (modelData) =>
      mlNodeApi.uploadModel(
        modelData.baseUrl,
        modelData.flowSlug,
        modelData.file,
      ),
    ...options,
  });
};

const PARSE_MODEL_REFETCH_INTERVAL_MS = 1000;

type ParsingError = ParsedModelDataError["error"];
export type BackendErrorData = {
  detail: string;
};

export const useParsedModelData = (
  baseUrl: string | undefined,
  flowSlug: string | undefined,
  modelUuid: string | undefined,
) => {
  return useQuery<
    ParsedModelDataPending | ParsedModelDataSuccess,
    AxiosError<BackendErrorData> | ParsingError
  >({
    queryKey: ["model", modelUuid!],
    queryFn: () => mlNodeApi.getParsedModel(baseUrl!, flowSlug!, modelUuid!),
    enabled: !!modelUuid,
    retry: false,
    refetchInterval: (query) => {
      // refetch if status is pending
      // data is the latest successful response
      // so we need to check the current state status to be not error
      if (
        query.state.data?.type === "pending" &&
        query.state.status !== "error"
      ) {
        return PARSE_MODEL_REFETCH_INTERVAL_MS;
      }

      return false;
    },
  });
};

type UseReviewCasesOptions = {
  enabled?: boolean;
};

export const useReviewCases = (
  baseUrl: string | undefined,
  flowSlug: string | undefined,
  params: ListReviewCasesParams = {},
  options: UseReviewCasesOptions = { enabled: true },
) =>
  useInfiniteQuery<
    ListReviewCasesResponse,
    Error,
    InfiniteData<ListReviewCasesResponse>,
    QueryKey,
    string | undefined
  >({
    queryKey: ["reviewCases", baseUrl, flowSlug, JSON.stringify(params)],
    queryFn: async ({ pageParam }): Promise<ListReviewCasesResponse> =>
      FlowRouterReviewCasesEndpoint.getCases(baseUrl!, flowSlug!, {
        ...params,
        before_timestamp: pageParam ?? params.before_timestamp,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.cases.length > 0 ? lastPage.page_min_timestamp : undefined,
    initialPageParam: undefined,
    enabled: options.enabled && !!baseUrl && !!flowSlug,
    staleTime: 0,
    // If we don't refetch on mount, changing the filters may show old of date data
    refetchOnMount: true,
    refetchInterval: 20 * 1000,
  });

export const useReviewCase = (
  baseUrl: string | undefined,
  flowSlug: string,
  caseId: string,
  options?: Omit<
    UseQueryOptions<ReviewCaseWithDecisionData, Error>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery<ReviewCaseWithDecisionData, Error>({
    queryKey: ["reviewCase", baseUrl, flowSlug, caseId],
    queryFn: () =>
      FlowRouterReviewCasesEndpoint.getCase(baseUrl!, flowSlug, caseId),
    enabled: !!baseUrl,
    retry: false,
    ...options,
  });

export const useReviewCasesAvailableFilters = (
  baseUrl: string | undefined,
  flowSlug: string | undefined,
  params: Pick<
    ListReviewCasesParams,
    "after_timestamp" | "before_timestamp" | "environment" | "statuses"
  > = {},
) =>
  useQuery({
    queryKey: [
      "reviewCasesAvailableFilters",
      baseUrl,
      flowSlug,
      params.after_timestamp,
      params.before_timestamp,
      params.environment,
      params.statuses,
    ],
    queryFn: () =>
      FlowRouterReviewCasesEndpoint.getFilters(baseUrl!, flowSlug!, params),
    enabled: !!baseUrl && !!flowSlug,
    refetchOnMount: true,
  });

const updateCase = (
  draft: Draft<ReviewCaseWithDecisionData | ReviewCase>,
  patch: CasePatchBody,
) => {
  if (patch.assignee !== undefined) {
    draft.assignee = patch.assignee;
  }
  if (patch.status !== undefined) {
    draft.status = patch.status;
  }
  if (patch.response_data !== undefined) {
    draft.response.data = patch.response_data;
  }
};

const updateCaseInCasesList = (
  cache: Draft<InfiniteData<ListReviewCasesResponse>> | undefined,
  caseId: string,
  patch: CasePatchBody,
) => {
  cache?.pages.forEach((page) => {
    page.cases.forEach((reviewCase) => {
      if (reviewCase.id !== caseId) return;

      updateCase(reviewCase, patch);
    });
  });
};

type ReviewCaseMutationContext = {
  oldReviewCasesDataCaches?: [
    QueryKey,
    InfiniteData<ListReviewCasesResponse> | undefined,
  ][];
  oldReviewCaseCache?: ReviewCaseWithDecisionData;
};

export const useReviewCaseMutation = (
  baseUrl: string | undefined,
  flowSlug: string,
  caseId: string,
) => {
  const queryClient = useQueryClient();
  const casesQueryKey = ["reviewCases", baseUrl, flowSlug];
  const singleCaseQueryKey = ["reviewCase", baseUrl, flowSlug, caseId];

  return useMutation({
    mutationFn: ({ etag, ...data }: CasePatchBody) =>
      FlowRouterReviewCasesEndpoint.patchCase(
        baseUrl!,
        flowSlug!,
        caseId,
        data,
        etag,
      ),
    onMutate: async (rowPatch: CasePatchBody) => {
      const context: ReviewCaseMutationContext = {};

      // Update cases list cache
      await queryClient.cancelQueries({
        queryKey: casesQueryKey,
      });
      const oldReviewCasesDataCaches = queryClient.getQueriesData<
        InfiniteData<ListReviewCasesResponse>
      >({
        queryKey: casesQueryKey,
      });

      if (oldReviewCasesDataCaches) {
        const updatedInifiniteData = produce(
          oldReviewCasesDataCaches,
          (queries) => {
            queries.forEach(([_queryKey, reviewCases]) => {
              // Update the review case in every found cache
              updateCaseInCasesList(reviewCases, caseId, rowPatch);
            });
          },
        );

        updatedInifiniteData.forEach(([queryKey, updatedData]) => {
          queryClient.setQueryData(queryKey, updatedData);
        });

        context.oldReviewCasesDataCaches = oldReviewCasesDataCaches;
      }

      // Update single case cache
      await queryClient.cancelQueries({
        queryKey: singleCaseQueryKey,
      });
      const oldReviewCaseCache =
        queryClient.getQueryData<ReviewCaseWithDecisionData>(
          singleCaseQueryKey,
        );

      if (oldReviewCaseCache) {
        queryClient.setQueryData<ReviewCaseWithDecisionData>(
          singleCaseQueryKey,
          (cachedData) => {
            if (cachedData) {
              return produce(cachedData, (reviewCaseDraft) => {
                updateCase(reviewCaseDraft, rowPatch);
              });
            }
            return cachedData;
          },
        );

        context.oldReviewCaseCache = oldReviewCaseCache;
      }

      return context;
    },
    onError: (_, __, context) => {
      if (context) {
        context.oldReviewCasesDataCaches?.forEach(([queryKey, updatedData]) => {
          queryClient.setQueryData(queryKey, updatedData);
        });

        if (context.oldReviewCaseCache) {
          queryClient.setQueryData(
            singleCaseQueryKey,
            context.oldReviewCaseCache,
          );
        }
      }
    },
    onSuccess: (updatedCase) => {
      // Update the single case cache with the PATCH result
      queryClient.setQueryData<ReviewCaseWithDecisionData>(
        singleCaseQueryKey,
        (existingCase) => {
          if (!existingCase) return updatedCase;
          return {
            ...updatedCase,
            inspect_data: existingCase.inspect_data,
            external_data: existingCase.external_data,
          };
        },
      );
      // Update available filters in table
      queryClient.invalidateQueries({
        queryKey: ["reviewCasesAvailableFilters", baseUrl, flowSlug],
      });
      // If the assignee changes we want to update the summaries and the cases list
      queryClient.invalidateQueries({
        queryKey: ["reviewSummary", baseUrl, flowSlug],
      });
      queryClient.invalidateQueries({
        queryKey: casesQueryKey,
      });
    },
  });
};
export const useReviewSummary = (
  baseUrl: string | undefined,
  flowSlug: string | undefined,
  environment: "prod" | "sandbox" = "prod",
) =>
  useQuery({
    queryKey: ["reviewSummary", baseUrl, flowSlug, environment],
    queryFn: () =>
      FlowRouterReviewCasesEndpoint.getSummary(
        baseUrl!,
        flowSlug!,
        environment,
      ),
    enabled: !!baseUrl && !!flowSlug,
    refetchOnMount: true,
  });

export const useReviewCaseSubmit = (
  baseUrl: string | undefined,
  flowSlug: string,
  caseId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ etag }: { etag: string }) =>
      FlowRouterReviewCasesEndpoint.submit(baseUrl!, flowSlug!, caseId, etag),
    onSuccess: () => {
      // Update available filters in table
      queryClient.invalidateQueries({
        queryKey: ["reviewCasesAvailableFilters", baseUrl, flowSlug],
      });
      queryClient.invalidateQueries({
        queryKey: ["reviewCase", baseUrl, flowSlug, caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["reviewCases", baseUrl, flowSlug],
      });
    },
  });
};

export const useMultiversionTestRun = (
  baseUrl: string,
  flowId: string,
  flowSlug: string,
) => {
  return useMutation({
    mutationFn: async (
      params: Omit<
        CreateMultiversionTestRunParams,
        "flow_id" | "flow_slug" | "infer_types"
      >,
    ) => {
      let response = undefined;
      let retries = 4;
      while (
        response === undefined ||
        (response.status === 404 && retries-- > 0)
      ) {
        if (response !== undefined)
          await new Promise((r) => setTimeout(r, 1000));
        response = await RouterMultiversionTestRun.post(baseUrl, {
          ...params,
          flow_id: flowId,
          flow_slug: flowSlug,
          infer_types: true,
        });
      }
      return response.data;
    },
  });
};

export const useDeleteWorkspace = () => {
  return useMutation({
    mutationKey: ["workspaces"],
    mutationFn: (workspaceId: string) => {
      return workspacesApi.deleteWorkspaceApiV1WorkspacesWorkspaceIdDelete(
        workspaceId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

export const useUpdateFlowTags = (flowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tags: FlowTags) => {
      return flowsApi.updateFlowApiV1FlowsIdPatch(flowId, {
        tags: tags,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flowKeys.detail(flowId) });
      queryClient.invalidateQueries({ queryKey: flowKeys.all });
    },
  });
};
